/**
 * «Platforma haqida» — ishlash sxemalari (ЧТЗ 4.2.11.1: «qisqa matn va sxema»).
 *
 *   1. Fuqaro va tadbirkor yo'li: kirishsiz imkoniyatlar → xarita / murojaat /
 *      katalog → maydonga tegishli xizmat → OneID orqali shaxsiy xizmatlar.
 *   2. Ichki jarayon: o'rmon xo'jaliklari ma'lumotni shakllantiradi → tekshiruv
 *      va integratsiya → Kuzatuv, elektron xizmatlar, raqamli nazorat → ochiq portal.
 *
 * Sxema qatorlardan iborat: qatorda bir yoki bir nechta guruh (ustun), guruhda
 * ketma-ket bosqichlar; bosqichda bir nechta tugun bo'lsa — ular parallel.
 * Kenglik yetmasa ustunlar ustma-ust tushadi (telefon). Havolali tugun bosilsa
 * tegishli bo'lim ochiladi.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { useI18n } from "@/shared/i18n";
import { Icon } from "@/shared/ui/Icon";

type Text = { uz: string; ru: string; en: string };

interface FlowNode {
  label: Text;
  to?: string;
  /** Tugun ostidagi qo'shimcha bog'liq tugun (masalan «Yo'riqnomalar»). */
  sub?: FlowNode;
  tone?: "start" | "accent" | "result" | "side";
}

interface FlowGroup {
  title?: Text;
  /** Bosqichlar ketma-ket; massivdagi bir nechta tugun — parallel. */
  steps: FlowNode[][];
  /** Guruh kengligi ulushi (standart 1). */
  span?: number;
}

interface FlowRow {
  /** Oldingi qatordan o'tish izohi (strelka ustida). */
  via?: Text;
  groups: FlowGroup[];
}

interface Flow {
  key: string;
  tab: Text;
  lead: Text;
  rows: FlowRow[];
}

const n = (uz: string, ru: string, en: string, to?: string, extra: Partial<FlowNode> = {}): FlowNode => ({
  label: { uz, ru, en },
  to,
  ...extra,
});
const t3 = (uz: string, ru: string, en: string): Text => ({ uz, ru, en });

const CITIZEN: Flow = {
  key: "citizen",
  tab: t3("Fuqaro va tadbirkor uchun", "Для граждан и бизнеса", "For citizens and businesses"),
  lead: t3(
    "Ko'p imkoniyatlar ro'yxatdan o'tmasdan ochiladi: xarita, murojaat, yangiliklar va katalog. Ariza, to'lov va xarid kabi shaxsiy amallar uchun OneID orqali kiriladi — tanlangan xizmat yoki xarid kirgandan keyin davom etadi.",
    "Многое доступно без регистрации: карта, обращения, новости и каталог. Для личных действий — заявок, платежей и покупок — нужен вход через OneID; выбранная услуга или покупка продолжается после входа.",
    "Much is available without registering: the map, appeals, news and the catalogue. Personal actions such as applications, payments and purchases require signing in with OneID; the chosen service or purchase continues after sign-in.",
  ),
  rows: [
    {
      groups: [
        {
          steps: [
            [n("Fuqaro yoki tadbirkor", "Гражданин или предприниматель", "Citizen or entrepreneur", undefined, { tone: "start" })],
            [n("Smart Forestry bosh sahifasi", "Главная страница Smart Forestry", "Smart Forestry home page", "/")],
            [n("Kompyuter yoki telefon brauzeri", "Браузер компьютера или телефона", "Computer or phone browser")],
          ],
        },
      ],
    },
    {
      groups: [
        {
          title: t3("Kirishsiz foydalanish mumkin", "Доступно без входа", "Available without signing in"),
          steps: [
            [
              n("Ochiq interaktiv xarita", "Открытая интерактивная карта", "Open interactive map", "/xarita"),
              n("Murojaat yuborish", "Отправить обращение", "Send an appeal", "/murojaat"),
              n("Platforma haqida, yangiliklar va ekotadbirlar", "О платформе, новости и экомероприятия",
                "About, news and eco-events", "/yangiliklar", {
                  sub: n("Yo'riqnomalar, savol-javob va yordam", "Инструкции, вопросы-ответы и помощь",
                    "Guides, FAQ and help", "/haqida#yoriqnomalar"),
                }),
              n("«Elektron yashil olam» katalogi", "Каталог «Электронного зелёного мира»", "“Green World” catalogue",
                "/yashil-olam"),
            ],
          ],
        },
      ],
    },
    {
      groups: [
        {
          title: t3("Xaritada maydon va obyektlarni ko'rish", "Просмотр участков и объектов на карте",
            "Viewing plots and objects on the map"),
          span: 1.6,
          steps: [
            [n("Viloyat, tuman va o'rmon xo'jaligi bo'yicha qidirish", "Поиск по области, району и лесхозу",
              "Search by region, district and forestry", "/xarita")],
            [
              n("Hodisalar, ko'chatxonalar, vegetatsiya indekslari va tiklash maydonlari",
                "Происшествия, питомники, индексы вегетации и участки восстановления",
                "Incidents, nurseries, vegetation indices and restoration areas"),
              n("O'rmon fondidan foydalanish", "Использование лесного фонда", "Use of the forest fund", "/xizmatlar"),
            ],
            [
              n("Umumiy o'rmon fondi va ijara maydonlari", "Лесной фонд и участки для аренды",
                "Forest fund and plots for lease", "/xarita?xizmat=ijara"),
              n("Chorva, pichan, asalari va madaniy tadbir maydonlari",
                "Участки для выпаса, сенокоса, пчеловодства и мероприятий",
                "Plots for grazing, hay, beekeeping and events", "/xarita?xizmat=ruxsatnoma"),
              n("Dorivor o'simliklarni terish maydonlari", "Участки сбора лекарственных растений",
                "Medicinal plant collection areas", "/xarita?xizmat=osimliklar"),
            ],
            [n("Obyekt chegarasi va ochiq ma'lumotlar kartochkasi", "Границы объекта и карточка открытых данных",
              "Object boundary and open data card", undefined, { tone: "accent" })],
          ],
        },
        {
          title: t3("Muammo haqida xabar berish", "Сообщить о проблеме", "Reporting a problem"),
          steps: [
            [n("Hodisa turi, xaritadagi joy va telefon", "Тип происшествия, место на карте и телефон",
              "Incident type, location on the map and phone", "/murojaat")],
            [n("Izoh, foto yoki video qo'shish", "Комментарий, фото или видео", "Add a comment, photo or video")],
            [n("Murojaat qabul qilinadi: ID va SMS", "Обращение принято: ID и SMS", "Appeal accepted: ID and SMS")],
            [n("ID va telefonning oxirgi 4 raqami bilan holatni kuzatish",
              "Отслеживание по ID и последним 4 цифрам телефона",
              "Track status with the ID and last 4 phone digits", "/kuzatish", { tone: "result" })],
          ],
        },
        {
          title: t3("Mahsulot xarid qilish", "Покупка продукции", "Buying products"),
          steps: [
            [n("Ko'chat, o'rmon mahsuloti yoki xizmatni tanlash", "Выбор саженцев, лесной продукции или услуги",
              "Choose seedlings, forest products or a service", "/yashil-olam")],
            [n("Narx, mavjudlik, sotuvchi va hududga moslikni ko'rish",
              "Цена, наличие, продавец и соответствие региону",
              "Price, availability, seller and regional suitability")],
            [n("Sotib olish", "Купить", "Buy", undefined, { tone: "accent" })],
          ],
        },
      ],
    },
    {
      via: t3("Maydonga tegishli xizmat", "Услуга для участка", "Service for the plot"),
      groups: [
        {
          title: t3("Maydondan foydalanish xizmatlari", "Услуги по использованию участков", "Plot use services"),
          steps: [
            [n("Tegishli xizmatni tanlash", "Выбор нужной услуги", "Choose the relevant service", "/xizmatlar")],
            [
              n("Ruxsatnoma yoki litsenziya tizimi", "Система разрешений или лицензий", "Permit or licence system",
                "/xizmatlar/ruxsatnoma"),
              n("E-AUKSION savdosi", "Торги E-AUKSION", "E-AUKSION auction", "/xizmatlar/ijara"),
            ],
            [n("Manba tizimda ariza yoki savdo jarayoni", "Заявка или торги в профильной системе",
              "Application or auction in the source system")],
          ],
        },
      ],
    },
    {
      via: t3(
        "Shaxsiy amal uchun kirish · integratsiya orqali shaxsiy natijalar · shaxsiy kabinet",
        "Вход для личных действий · личные результаты через интеграцию · личный кабинет",
        "Sign-in for personal actions · personal results via integration · personal account",
      ),
      groups: [
        {
          title: t3("OneID orqali shaxsiy xizmatlar", "Личные услуги через OneID", "Personal services via OneID"),
          steps: [
            [n("OneID orqali kirish", "Вход через OneID", "Sign in with OneID", "/kirish", { tone: "accent" })],
            [n("Tanlangan xizmat yoki xaridni davom ettirish", "Продолжение выбранной услуги или покупки",
              "Continue the chosen service or purchase")],
            [
              n("Mening arizalarim va ruxsatnomalarim", "Мои заявки и разрешения", "My applications and permits"),
              n("Mening ijaralarim: shartnoma, hisob va to'lovlar", "Мои аренды: договор, счета и платежи",
                "My leases: contract, invoices and payments", "/xizmatlar/billing"),
              n("Buyurtma: miqdor, yetkazish va qo'shimcha xizmatlar", "Заказ: количество, доставка и доп. услуги",
                "Order: quantity, delivery and extra services", "/xizmatlar/market", {
                  sub: n("Sotuvchi tasdig'i, shartnoma va invoys", "Подтверждение продавца, договор и счёт",
                    "Seller confirmation, contract and invoice"),
                }),
            ],
            [n("Onlayn to'lov", "Онлайн-оплата", "Online payment")],
            [n("Yetkazib berish yoki xizmatni bajarish", "Доставка или оказание услуги", "Delivery or service fulfilment")],
            [n("Natija, elektron chek va xabarnomalar", "Результат, электронный чек и уведомления",
              "Result, e-receipt and notifications", undefined, { tone: "result" })],
          ],
        },
      ],
    },
  ],
};

const INTERNAL: Flow = {
  key: "internal",
  tab: t3("Ichki jarayon (xodimlar)", "Внутренний процесс (сотрудники)", "Internal process (staff)"),
  lead: t3(
    "Ma'lumotni o'rmon xo'jaliklari va vakolatli xodimlar shakllantiradi, davlat tizimlari, sun'iy yo'ldosh, kamera va datchiklar to'ldiradi. Ma'lumot tekshirilgach ish jarayonlariga uzatiladi. Fuqarolarga faqat ochiq e'lon qilishga tasdiqlangan ma'lumot chiqadi.",
    "Данные формируют лесхозы и уполномоченные сотрудники, дополняют госсистемы, спутники, камеры и датчики. После проверки данные идут в рабочие процессы. Гражданам публикуется только то, что утверждено для открытого доступа.",
    "Data is produced by forestry enterprises and authorised staff and enriched by state systems, satellites, cameras and sensors. After verification it feeds working processes. Citizens only see data approved for public release.",
  ),
  rows: [
    {
      groups: [
        { steps: [[n("O'rmon xo'jaliklari va vakolatli xodimlar", "Лесхозы и уполномоченные сотрудники",
          "Forestry enterprises and authorised staff", undefined, { tone: "start" })]] },
      ],
    },
    {
      groups: [
        {
          span: 0.8,
          steps: [[n("Ma'muriyat va xavfsizlik: rollar, huquqlar va audit", "Администрирование и безопасность: роли, права и аудит",
            "Administration and security: roles, rights and audit", undefined, { tone: "side" })]],
        },
        {
          title: t3("Ma'lumotlarni shakllantirish", "Формирование данных", "Data entry"),
          span: 2.4,
          steps: [[
            n("Geoma'lumot va kartografiya", "Геоданные и картография", "Geodata and mapping"),
            n("Ijara yerlarini boshqarish", "Управление арендой земель", "Lease management"),
            n("O'rmonni tiklash va yer tuzish hisobi", "Учёт лесовосстановления и землеустройства",
              "Restoration and land management records"),
            n("Mahsulot va resurslar hisobi", "Учёт продукции и ресурсов", "Products and resources records"),
          ]],
        },
        {
          span: 0.8,
          steps: [[n("Davlat tizimlari, sun'iy yo'ldoshlar, kamera va datchiklar", "Госсистемы, спутники, камеры и датчики",
            "State systems, satellites, cameras and sensors", undefined, { tone: "side" })]],
        },
      ],
    },
    {
      groups: [{ steps: [[n("Ma'lumotlarni tekshirish va integratsiya", "Проверка и интеграция данных",
        "Data verification and integration", undefined, { tone: "accent" })]] }],
    },
    {
      groups: [
        {
          steps: [
            [n("Xodimlar korporativ kabineti", "Корпоративный кабинет сотрудников", "Staff corporate workspace", "/kirish")],
          ],
        },
        {
          steps: [
            [n("Hodisalarni boshqarish va Kuzatuv", "Управление происшествиями и «Кузатув»", "Incident management and Kuzatuv")],
            [n("Mas'ulga topshiriq, ijro va natija", "Поручение ответственному, исполнение и результат",
              "Assignment, execution and result")],
          ],
        },
        {
          steps: [[n("Elektron xizmatlar: ruxsatnoma, ijara, savdo va to'lovlar", "Электронные услуги: разрешения, аренда, торговля и платежи",
            "E-services: permits, leases, trade and payments", "/xizmatlar")]],
        },
      ],
    },
    {
      groups: [
        {
          steps: [
            [
              n("Raqamli nazorat: vakolatli organlar uchun", "Цифровой контроль для уполномоченных органов",
                "Digital oversight for authorised bodies", undefined, { tone: "side" }),
              n("Ochiq e'lon qilishga tasdiqlangan ma'lumotlar", "Данные, утверждённые для публикации",
                "Data approved for public release"),
            ],
            [n("Ommaviy portal va mobil veb-versiya", "Публичный портал и мобильная веб-версия",
              "Public portal and mobile web", "/")],
            [n("Fuqaroga xarita, axborot va xizmat natijalari", "Карта, информация и результаты услуг для граждан",
              "Map, information and service results for citizens", "/xarita", { tone: "result" })],
          ],
        },
      ],
    },
  ],
};

const FLOWS = [CITIZEN, INTERNAL];

function pick(text: Text, lang: string): string {
  return text[lang as keyof Text] || text.uz;
}

function NodeBox({ node, lang }: { node: FlowNode; lang: string }) {
  const className = `flow-node${node.tone ? ` is-${node.tone}` : ""}${node.to ? " is-link" : ""}`;
  const label = pick(node.label, lang);
  const box = node.to ? (
    <Link className={className} to={node.to}>
      {label}
    </Link>
  ) : (
    <span className={className}>{label}</span>
  );
  if (!node.sub) return box;
  return (
    <span className="flow-node-stack">
      {box}
      <span className="flow-arrow is-small" aria-hidden="true">
        <Icon name="chevronDown" size={14} />
      </span>
      <NodeBox node={node.sub} lang={lang} />
    </span>
  );
}

function FlowDiagram({ flow, lang }: { flow: Flow; lang: string }) {
  return (
    <ol className="flow" aria-label={pick(flow.tab, lang)}>
      {flow.rows.map((row, rowIndex) => (
        <li className="flow-row-wrap" key={rowIndex}>
          {rowIndex > 0 ? (
            <div className="flow-link" aria-hidden={!row.via}>
              <Icon name="arrowRight" size={18} className="flow-link-icon" />
              {row.via ? <span className="flow-via">{pick(row.via, lang)}</span> : null}
            </div>
          ) : null}
          <div className="flow-row">
            {row.groups.map((group, groupIndex) => (
              <section
                key={groupIndex}
                className={`flow-group${group.title ? " has-title" : ""}`}
                style={{ flexGrow: group.span ?? 1 }}
                aria-label={group.title ? pick(group.title, lang) : undefined}
              >
                {group.title ? <h4>{pick(group.title, lang)}</h4> : null}
                {group.steps.map((step, stepIndex) => (
                  <div className="flow-step-wrap" key={stepIndex}>
                    {stepIndex > 0 ? (
                      <span className="flow-arrow" aria-hidden="true">
                        <Icon name="chevronDown" size={16} />
                      </span>
                    ) : null}
                    <div className={`flow-step${step.length > 1 ? " is-parallel" : ""}`}>
                      {step.map((node, nodeIndex) => (
                        <NodeBox key={nodeIndex} node={node} lang={lang} />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function AboutFlows() {
  const { lang } = useI18n();
  const [active, setActive] = useState(FLOWS[0].key);
  const flow = FLOWS.find((item) => item.key === active) ?? FLOWS[0];

  return (
    <div className="about-flows">
      <div className="segmented" role="tablist">
        {FLOWS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={item.key === active}
            aria-pressed={item.key === active}
            onClick={() => setActive(item.key)}
          >
            {pick(item.tab, lang)}
          </button>
        ))}
      </div>
      <p className="about-flow-lead">{pick(flow.lead, lang)}</p>
      <div role="tabpanel">
        <FlowDiagram flow={flow} lang={lang} />
      </div>
    </div>
  );
}
